import { useMemo, useSyncExternalStore } from 'react'

/**
 * The viewer's own volume on the watch screen (docs/features/stage-volume.md): local to this browser, for every event,
 * never sent anywhere. `level` is 1–100 and never 0: taking the slider to the start mutes instead, so unmuting goes
 * back to the last level heard.
 */
export interface ViewerVolume {
  level: number
  muted: boolean
}

export interface ViewerVolumeControls extends ViewerVolume {
  /** A level from the slider: 0 mutes and keeps the last level; anything else unmutes at that level. */
  setLevel(level: number): void
  setMuted(muted: boolean): void
}

const KEY = 'sb_watch_volume'
const FULL: ViewerVolume = { level: 100, muted: false }

/** A stored volume, or full and unmuted for nothing stored or anything unreadable. */
export function parseViewerVolume(raw: string | null): ViewerVolume {
  if (!raw) return FULL
  try {
    const value = JSON.parse(raw) as Partial<ViewerVolume> | null
    const level = typeof value?.level === 'number' && Number.isFinite(value.level) ? Math.round(value.level) : NaN
    return {
      level: level >= 1 && level <= 100 ? level : FULL.level,
      muted: value?.muted === true,
    }
  } catch {
    return FULL
  }
}

// Where the volume lives while the browser refuses storage (a blocked site, some private windows): this page only.
let unstored: string | null = null
let snapshot: { raw: string | null; volume: ViewerVolume } = { raw: null, volume: FULL }
const listeners = new Set<() => void>()

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    return unstored
  }
}

/** The same object while what's stored doesn't change, as useSyncExternalStore needs. */
function getSnapshot(): ViewerVolume {
  const raw = readRaw()
  if (raw !== snapshot.raw) snapshot = { raw, volume: parseViewerVolume(raw) }
  return snapshot.volume
}

function save(volume: ViewerVolume) {
  const raw = JSON.stringify(volume)
  unstored = raw
  try {
    window.localStorage.setItem(KEY, raw)
  } catch {
    // Kept for this page only.
  }
  listeners.forEach((listener) => listener())
}

// `storage` fires when another tab of this browser changes it, so every open watch screen follows.
function subscribe(listener: () => void) {
  listeners.add(listener)
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

function setLevel(level: number) {
  const rounded = Math.round(Math.min(100, Math.max(0, level)))
  save(rounded === 0 ? { ...getSnapshot(), muted: true } : { level: rounded, muted: false })
}

function setMuted(muted: boolean) {
  save({ ...getSnapshot(), muted })
}

/** The viewer's volume and its setters, shared by every component that uses it and every tab of this browser. */
export function useViewerVolume(): ViewerVolumeControls {
  const volume = useSyncExternalStore(subscribe, getSnapshot, () => FULL)
  return useMemo(() => ({ ...volume, setLevel, setMuted }), [volume])
}
